import { getAuth } from 'firebase/auth';
import { uploadChatAttachment } from '../firebase/storage';
import { getApiBaseUrl, getApiUrl, getApiHeaders } from '../utils/apiConfig';
import { fetchWithRetry, calcApiTimeout } from '../utils/fetchWithRetry';
import { toChatApiModelId } from '../constants/aiModels';
import { attachOutlineLayoutToArtifactState } from '../utils/artifactState';
import { takeChatActionReport, configureChatActionReporter } from '../utils/chatActionReport';
import { takeChatImageState } from '../utils/chatImageState';
import { takeChatWorkState } from '../utils/chatWorkState';
import { getTrackingErrorCode } from '../utils/trackingErrorCode';
import { keepablePartialText } from '../utils/chatPartialAnswer';
import { readSseJson } from '../utils/sseJsonStream';
import {
  newChatTurnId,
  recoverChatTurn,
  cancelChatTurn,
} from './chatTurnRecovery';

/**
 * 채팅 스트림 실패를 말풍선 문구와 계측 code 로 바꾼다.
 * 메시지가 그대로 AI 답변 자리에 표시되므로 HTTP 원문·브라우저 영어 메시지("Load failed")를 넣지 않는다.
 */
const chatStreamError = (message, code, { retryable = false, serverMessage = '' } = {}) => {
  const error = new Error(message);
  error.errorInfo = { code, retryable, serverMessage };
  return error;
};

const chatHttpError = async (response) => {
  const raw = await response.text().catch(() => '');
  let detail = null;
  try {
    detail = JSON.parse(raw)?.detail ?? null;
  } catch (_) {
    detail = null;
  }
  const detailCode = detail && typeof detail === 'object' && typeof detail.code === 'string' ? detail.code : '';
  const detailMessage = typeof detail === 'string'
    ? detail
    : (detail && typeof detail === 'object' && typeof detail.message === 'string' ? detail.message : '');
  const { status } = response;
  const code = detailCode || (status === 429 ? 'rate_limited' : `http_${status}`);
  const fallback = status === 429
    ? '요청이 많아요. 잠시 후 다시 보내주세요.'
    : status >= 500
      ? '서버에 잠시 문제가 생겼어요. 잠시 후 다시 보내주세요.'
      : '메시지를 보내지 못했어요. 새로고침한 뒤 다시 보내주세요.';
  // 서버가 사용자용 한글 문구를 준 경우(4xx detail)만 그대로 쓴다.
  const message = status < 500 && /[가-힣]/.test(detailMessage) ? detailMessage : fallback;
  return chatStreamError(message, code, {
    retryable: status === 429 || status >= 500,
    serverMessage: raw.slice(0, 300),
  });
};

// API 베이스 URL 설정 - 중앙집중식 설정 사용
const API_BASE_URL = getApiBaseUrl();

export const sendChatActionReportNow = async (body) => {
  const currentUser = getAuth().currentUser;
  if (!currentUser) return;
  const token = await currentUser.getIdToken();
  await fetch(`${API_BASE_URL}/api/chat/client-action`, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
};
configureChatActionReporter(sendChatActionReportNow);

/**
 * 채팅 세션 초기화
 * @returns {Promise<Object>} 초기화된 세션 정보
 */
export const initChatSession = async () => {
  try {
    // API 엔드포인트 구성
    const endpoint = `${API_BASE_URL}/api/chat/init`;
    
    // API 요청
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'omit',
      mode: 'cors'
    });
    
    // 요청 실패 처리
    if (!response.ok) {
      const errorText = await response.text();
      console.error('채팅 세션 초기화 오류:', response.status, errorText);
      throw new Error(`채팅 세션을 초기화하는 중 오류가 발생했습니다. 상태 코드: ${response.status}`);
    }
    
    // 응답 파싱
    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error('채팅 세션 초기화 요청 실패:', error);
    throw error;
  }
};

/**
 * AI 응답을 가져오는 함수
 * @param {string} message - 사용자 메시지
 * @param {string} conversationId - 대화 ID (세션 ID)
 * @returns {Promise<Object>} AI 응답 객체
 */
export const getAIResponse = async (message, conversationId) => {
  try {
    // API 요청 데이터 구성
    const requestData = {
      message: message,
      session_id: conversationId
    };
    
    // API 엔드포인트 구성
    const endpoint = `${API_BASE_URL}/api/chat/message`;
    
    // API 요청
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestData),
      credentials: 'omit',
      mode: 'cors'
    });
    
    // 요청 실패 처리
    if (!response.ok) {
      const errorText = await response.text();
      console.error('AI 응답 오류:', response.status, errorText);
      throw new Error(`AI 응답을 가져오는 중 오류가 발생했습니다. 상태 코드: ${response.status}`);
    }
    
    // 응답 파싱
    const responseData = await response.json();
    return {
      session_id: responseData.session_id,
      response: responseData.last_message.content,
      messages: responseData.messages
    };
  } catch (error) {
    console.error('AI 응답 요청 실패:', error);
    throw error;
  }
};

/**
 * 채팅 세션 정보 가져오기
 * @param {string} sessionId - 세션 ID
 * @returns {Promise<Object>} 세션 정보
 */
export const getChatSession = async (sessionId) => {
  try {
    // API 엔드포인트 구성
    const endpoint = `${API_BASE_URL}/api/chat/${sessionId}`;
    
    // API 요청
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'omit',
      mode: 'cors'
    });
    
    // 요청 실패 처리
    if (!response.ok) {
      const errorText = await response.text();
      console.error('채팅 세션 조회 오류:', response.status, errorText);
      throw new Error(`채팅 세션을 조회하는 중 오류가 발생했습니다. 상태 코드: ${response.status}`);
    }
    
    // 응답 파싱
    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error('채팅 세션 조회 요청 실패:', error);
    throw error;
  }
};

/**
 * 🔥 Phase 2: 어시스턴트 스트리밍 응답을 가져오는 함수
 * @param {string} message - 사용자 메시지
 * @param {string} content - 분석할 콘텐츠 (학습지/수행평가)
 * @param {string} contentType - 콘텐츠 타입
 * @param {Object} metadata - 콘텐츠 메타데이터
 * @param {Array} conversationHistory - 이전 대화 기록
 * @param {function} onChunk - 응답 청크 처리 콜백
 * @param {function} onComplete - 완료 콜백
 * @param {function} onError - 에러 콜백
 */
export const getAssistantResponseStream = async (
  message, 
  content, 
  contentType, 
  metadata, 
  conversationHistory, 
  onChunk, 
  onComplete, 
  onError
) => {
  try {
    // API 요청 데이터 구성
    const requestData = {
      message: message,
      content: content,
      content_type: contentType,
      metadata: metadata || {},
      conversation_history: conversationHistory || []
    };
    
    // API 엔드포인트 구성
    const endpoint = `${API_BASE_URL}/api/assistant/chat-stream`;
    
    // EventSource 대신 fetch로 스트리밍 처리
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache'
      },
      body: JSON.stringify(requestData),
      credentials: 'omit',
      mode: 'cors'
    });
    
    if (!response.ok) {
      throw new Error(`스트리밍 요청 실패: ${response.status}`);
    }
    
    for await (const data of readSseJson(response.body)) {
      try {
        if (data.type === 'analysis') {
          // 콘텐츠 분석 정보
          onChunk({
            type: 'analysis',
            contentAnalysis: data.content_analysis,
            contentType: data.content_type
          });
        } else if (data.type === 'start') {
          // 스트리밍 시작
          onChunk({
            type: 'start',
            message: data.message
          });
        } else if (data.type === 'content') {
          // 텍스트 청크
          onChunk({
            type: 'content',
            content: data.content,
            fullContent: data.full_content
          });
        } else if (data.type === 'complete') {
          // 완료
          onComplete({
            fullResponse: data.full_response,
            tokenUsage: data.token_usage,
            memory_update: data.memory_update,
            suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
            personalization_used: data.personalization_used || null
          });
          return;
        } else if (data.type === 'error') {
          // 에러
          onError(new Error(data.error));
          return;
        } else {
          // 🆕 알려지지 않은 청크 타입은 그대로 통과 (hwpx_edit_result 등)
          onChunk(data);
        }
      } catch (handleError) {
        console.warn('스트림 청크 처리 오류:', handleError, data);
      }
    }
  } catch (error) {
    console.error('어시스턴트 스트리밍 응답 오류:', error);
    onError(error);
  }
};

/**
 * 🚀 AI 상담 스트리밍 응답을 가져오는 함수 (새로 구현, 카테고리 지원 추가)
 * @param {string} message - 사용자 메시지
 * @param {string} conversationId - 대화 ID (세션 ID)
 * @param {string} category - AI 카테고리 (behavior, parent, guidance, counseling, general)
 * @param {Object} analysisContext - 분석 컨텍스트 (오박사 상담용)
 * @param {function} onChunk - 응답 청크 처리 콜백
 * @param {function} onComplete - 완료 콜백
 * @param {function} onError - 에러 콜백
 * @param {AbortSignal} signal - 요청 취소 신호
 */
const inferRequestedArtifactFormat = (message) => {
  const value = String(message || '');
  if (/(?:pdf|피디에프)/i.test(value)) return 'pdf';
  if (/(?:docx|word|워드)/i.test(value)) return 'docx';
  if (/(?:hwpx)/i.test(value)) return 'hwpx';
  if (/(?:hwp)/i.test(value)) return 'hwp';
  if (/(?:한글\s*(?:파일|문서|양식))/i.test(value)) return 'hwpx';
  if (/(?:xlsx|excel|엑셀)/i.test(value)) return 'xlsx';
  if (/(?:csv)/i.test(value)) return 'csv';
  return '';
};

// 채팅 파일 아티팩트(chat_file_artifacts)의 형식. 학습지/PPT 항목은 format 이 없어 제외된다.
const CHAT_ARTIFACT_FORMATS = new Set(['xlsx', 'csv', 'hwpx', 'hwp', 'docx', 'pdf']);
const CHAT_ARTIFACT_FILE_ID_RE = /^[0-9a-f]{32}$/i;

/**
 * 서버가 "직전 파일" 로 쓸 수 있는 가장 최근 채팅 파일 ID.
 *
 * - 변환 여부는 프론트가 판단하지 않는다. 답변 모델이 file-artifact 메타에
 *   source="previous_file" 을 선언했을 때만 서버가 이 ID 를 사용한다. 그래서 항상 보낸다.
 * - format 이 없는 항목(학습지/PPT 등)은 chat_file_artifacts 가 아니므로 건너뛴다.
 * - 서버는 저장된 원문(source.md)으로 대상 형식을 다시 렌더하므로 형식 간 호환표는 필요 없다.
 *   hwp 요청만 rhwp 바이너리 변환이 가능한 hwpx 를 우선한다 (형식 힌트일 뿐 판단이 아니다).
 */
export const getLatestChatArtifactId = (messages = [], userMessage = '') => {
  const target = inferRequestedArtifactFormat(userMessage);

  const candidates = [];
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const artifacts = Array.isArray(messages[index]?.artifacts)
      ? messages[index].artifacts
      : [];
    for (
      let artifactIndex = artifacts.length - 1;
      artifactIndex >= 0;
      artifactIndex -= 1
    ) {
      const fileId = String(artifacts[artifactIndex]?.file_id || '').trim();
      const format = String(artifacts[artifactIndex]?.format || '').toLowerCase();
      if (CHAT_ARTIFACT_FILE_ID_RE.test(fileId) && CHAT_ARTIFACT_FORMATS.has(format)) {
        candidates.push({ fileId, format });
      }
    }
  }
  if (candidates.length === 0) return null;

  if (target === 'hwp') {
    const hwpxSource = candidates.find((item) => item.format === 'hwpx');
    if (hwpxSource) return hwpxSource.fileId;
  }
  return candidates[0].fileId;
};

/** 서버 complete 이벤트 → onComplete 인자. 연결이 끊긴 턴을 되찾을 때도 같은 모양으로 넘긴다. */
const toCompletePayload = (data, fallbackFullResponse = '', turnId = null) => ({
  sessionId: data.session_id,
  // 서버가 떠난 화면 대신 저장할 때와 같은 메시지 문서 id 를 만드는 데 쓴다
  turnId,
  fullResponse: data.full_response || fallbackFullResponse,
  message: data.message,
  timestamp: data.timestamp,
  web_search_sources: data.web_search_sources,
  web_search_queries: data.web_search_queries,
  reasoning_steps: data.reasoning_steps,
  response_id: data.response_id,
  response_chain_len: data.response_chain_len,
  effective_model: data.effective_model,
  actual_response_model: data.actual_response_model,
  reasoning_effort: data.reasoning_effort,
  usage: data.usage,
  latency: data.latency,
  estimated_cost_usd: data.estimated_cost_usd,
  memory_update: data.memory_update,
  // 🧠 AI 제안 카드(memory_proposal 등) — 메시지에 함께 저장돼 새로고침 후에도 유지된다.
  suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
  // 🧠 투명화(K-1): 이 답변에 반영된 선생님 정보 필드 이름·기억 개수(값 없음)
  personalization_used: data.personalization_used || null,
  // 📁 이 답변이 읽은 내 자료함 자료 [{id, title}] — 답 아래 '내 자료함에서 … 참고' 한 줄
  library_references: Array.isArray(data.library_references) ? data.library_references : [],
  // 🗂️ 이 답변이 읽은 지난 대화 [{id, title, date}] — 답 아래 '지난 대화 … 참고' 한 줄
  past_chat_references: Array.isArray(data.past_chat_references) ? data.past_chat_references : [],
  artifacts: Array.isArray(data.artifacts) ? data.artifacts : [],
  // 📎 파일을 만들겠다고만 하고 실제 파일이 없는 답변 — '지금 파일로 만들기' 버튼을 띄운다
  promise_without_file: data.promise_without_file === true,
  // ✏️ 첨부 파일을 고쳤다/고치겠다고만 하고 실제로는 안 바뀐 답변 — '지금 반영하기' 버튼을 띄운다
  edit_not_applied: data.edit_not_applied === true,
  // 'artifact' 면 '지금 반영하기'가 첨부 파일 대신 학습지·PPT 초안 수정을 다시 부른다
  edit_not_applied_target: data.edit_not_applied_target === 'artifact' ? 'artifact' : undefined,
  // ⏳ 만들겠다/바꾸겠다고만 하고 실행 신호가 없던 답변 — '지금 바로 하기' 버튼을 띄운다
  action_not_taken: data.action_not_taken === true,
  // ✏️ 서버 도구(edit_artifact)가 이 답변 안에서 학습지·PPT 초안을 고친 결과 — 화면이 그대로 적용한다
  artifact_edit: data.artifact_edit && typeof data.artifact_edit === 'object' ? data.artifact_edit : null,
  // 🃏 첫 턴 되묻기 카드 — 있으면 이 턴은 본문 없이 카드로 끝난다(답변은 다음 사용자 메시지)
  clarify_card: data.clarify_card && typeof data.clarify_card === 'object' ? data.clarify_card : null,
  // 🧩 P5 자동 스킬 라벨 {skill,label,text,source} — 자유 대화에서 전문 모듈이 적용된 턴에만 온다
  applied_skill: data.applied_skill && typeof data.applied_skill === 'object' && data.applied_skill.skill
    ? data.applied_skill
    : null,
  // 🖼️ 본문 [[image_strip:N]] 자리의 웹 이미지 묶음(최종본). 없으면 undefined → 스트리밍 중 받은 것을 쓴다.
  web_images: Array.isArray(data.web_images) ? data.web_images : undefined,
  // 🃏 본문 [[rich:N]] 자리의 사진 추천 카드(최종본). 없으면 undefined → 스트리밍 중 받은 것을 쓴다.
  rich_blocks: Array.isArray(data.rich_blocks) ? data.rich_blocks : undefined,
  // 🧭 답변 끝 조건 고르기 양식
  followup_form: data.followup_form && typeof data.followup_form === 'object' ? data.followup_form : null,
  // ⏭️ 답변 아래 다음 질문 칩 [{text, kind}]
  next_chips: Array.isArray(data.next_chips) ? data.next_chips : []
});

export const getAIResponseStream = async (
  message, 
  conversationId, 
  category = 'general',
  analysisContext = null,
  onChunk, 
  onComplete, 
  onError, 
  signal = null,
  userId = null,
  conversationFirebaseId = null,
  selectedModel = null,
  isProUser = false,
  includeAttachedFiles = false,
  attachedFileIds = null,
  existingOutline = null,   // 🆕 ppt_maker/worksheet_gen: 현재 활성 outline 요약 주입용
  generateTitle = false,
  sourceArtifactId = null,
  workspaceTargetFileId = null,
  // 학습지/PPT 산출물 현황 요약. 모양은 utils/artifactState.buildArtifactState 참고.
  // { has_artifact, latest: {format,pageCount,filename,createdAt,sourceMode}|null, available_formats: [], mode }
  artifactState = null,
  // 📎 이번 메시지에서 새로 첨부한 파일 ID (attachedFileIds 의 부분집합).
  //   서버가 "이번 턴 새 첨부"를 모델에 표시해 '파일이 안 보인다'는 오답을 막는다.
  newlyAttachedFileIds = null,
  // 🧩 P5 자동 스킬: { activeSkill: 직전 턴 라벨의 스킬(대화 안 유지), skillOverride: '바꾸기'로 고른 스킬('none' 포함) }
  skillContext = null,
  // 화면 실행 보고(chatActionReport)를 찾을 화면 대화 ID — 서버로 보내지 않는다(익명은 conversation_id 를 안 보냄).
  reportConversationId = null,
  // 익명 대화 최근 기록 — utils/anonymousChatHistory.buildAnonymousChatHistory 결과 (이미 maskCriticalPII 적용)
  anonymousHistory = null,
  // 멈춤 때 받은 글이 있으면 지우지 않고 남긴다(호출부가 같은 턴 문서 id 로 저장한다). 서버에도 알려 같은 id 로 확인 저장하게 한다.
  keepPartialOnStop = false,
  // 다시 만들기·고쳐 보내기: { action: 'regenerate'|'edit_resend', replaceDocIds: [옛 사용자·답변 문서 id] }
  //   서버가 그 턴을 모델 기록에서 빼고(services/chat_turn_replace.py), 대신 저장할 때도 지운다.
  turnAction = null
) => {
  let fullResponse = '';
  let sessionId = conversationId;
  let retryCount = 0;
  const MAX_AUTO_RETRY = 1;
  const apiSelectedModel = toChatApiModelId(selectedModel);
  const newlyAttachedSubset = (Array.isArray(newlyAttachedFileIds) && Array.isArray(attachedFileIds))
    ? newlyAttachedFileIds.filter(id => attachedFileIds.includes(id))
    : [];

  const requestData = {
    message: message,
    session_id: conversationId,
    category: category,
    is_pro_user: isProUser,
    // 무료 사용자도 자료함 텍스트 자료는 붙일 수 있다 (서버가 소유권·종류를 다시 검증해 걸러낸다).
    include_attached_files: includeAttachedFiles && (isProUser || (Array.isArray(attachedFileIds) && attachedFileIds.length > 0)),
    ...(attachedFileIds && attachedFileIds.length > 0 && { attached_file_ids: attachedFileIds }),
    ...(newlyAttachedSubset.length > 0 && { newly_attached_file_ids: newlyAttachedSubset }),
    ...(apiSelectedModel && { selected_model: apiSelectedModel }),
    ...(analysisContext && { analysis_context: analysisContext }),
    ...(userId && { user_id: userId }),
    ...(conversationFirebaseId && { conversation_id: conversationFirebaseId }),
    // 🆕 현재 활성 outline 전달 — 채팅 AI가 "N번 슬라이드"의 실체를 정확히 인식
    ...(existingOutline && { existing_outline: existingOutline }),
    ...(generateTitle && { generate_title: true }),
    ...(sourceArtifactId && { source_artifact_id: sourceArtifactId }),
    ...(workspaceTargetFileId && { workspace_target_file_id: workspaceTargetFileId }),
    ...(() => {
      const enriched = attachOutlineLayoutToArtifactState(artifactState, existingOutline);
      return enriched && typeof enriched === 'object' ? { artifact_state: enriched } : {};
    })(),
    // 🧩 P5 자동 스킬 — 자유 대화에서만 서버가 쓴다. 직전 라벨 스킬을 되돌려 주면 대화 안에서 유지된다.
    ...(skillContext?.activeSkill && { active_skill: String(skillContext.activeSkill) }),
    ...(skillContext?.activeSkill && skillContext?.activeSkillLocked && { active_skill_locked: true }),
    ...(skillContext?.skillOverride && { skill_override: String(skillContext.skillOverride) }),
    ...(Array.isArray(anonymousHistory?.history) && anonymousHistory.history.length > 0 && {
      anonymous_history: anonymousHistory.history,
      anonymous_prior_turns: Math.max(0, Number(anonymousHistory.priorUserTurns) || 0),
    }),
    ...(() => {
      const report = takeChatActionReport(reportConversationId || conversationFirebaseId || conversationId);
      return report ? { client_report: report } : {};
    })(),
    ...(() => {
      const imageState = takeChatImageState(reportConversationId || conversationFirebaseId || conversationId);
      return imageState ? { image_state: imageState } : {};
    })(),
    ...(() => {
      const workState = takeChatWorkState(reportConversationId || conversationFirebaseId || conversationId);
      return workState ? { work_state: workState } : {};
    })(),
    ...(turnAction?.action && {
      client_action: turnAction.action,
      ...(Array.isArray(turnAction.replaceDocIds) && turnAction.replaceDocIds.length > 0 && {
        replace_message_ids: turnAction.replaceDocIds.slice(0, 4),
      }),
    }),
  };

  const endpoint = `${API_BASE_URL}/api/chat/message`;
  let authToken = null;
  try {
    const currentUser = getAuth().currentUser;
    authToken = currentUser ? await currentUser.getIdToken() : null;
  } catch (error) {
    console.warn('채팅 인증 토큰을 가져오지 못해 Free 권한으로 요청합니다.', error);
  }
  const getFreshToken = async () => {
    const currentUser = getAuth().currentUser;
    return currentUser ? currentUser.getIdToken() : authToken;
  };

  // 오류로 끝나도 이번 시도에서 받은 글은 화면이 지우지 않도록 함께 넘긴다(자동 재시도는 새로 받으므로 비운다).
  const withPartial = (errorObj) => {
    if (errorObj && fullResponse && !errorObj.partialResponse) errorObj.partialResponse = fullResponse;
    return errorObj;
  };

  const attemptStream = async () => {
    let streamCompleted = false;
    let streamErrored = false;
    // 로그인 턴만 서버가 결과를 남긴다(익명은 되찾을 수 없다).
    const turnId = authToken ? newChatTurnId() : null;
    let requestSent = false;
    let responseReceived = false;

    // 연결이 끊긴 턴을 서버 기록에서 되찾는다.
    //   'handled': 이 턴은 처리가 끝났다 · 'unknown': 서버에 이 턴이 없다(다시 보내도 된다)
    //   'timeout': 서버가 아직 만들고 있을 수 있다(다시 보내면 두 번 만든다)
    const recoverTurn = async () => {
      if (!turnId || !requestSent) return 'unknown';
      onChunk({
        type: 'status',
        statusType: 'reconnecting',
        message: responseReceived
          ? '연결이 잠깐 끊겼어요. 답변을 이어서 받아오고 있어요...'
          : '연결이 잠깐 끊겼어요. 보낸 메시지를 확인하고 있어요...',
      });
      const result = await recoverChatTurn({
        baseUrl: API_BASE_URL,
        turnId,
        getToken: getFreshToken,
        signal,
      });
      if (result.status === 'aborted') {
        const abortError = new Error('aborted');
        abortError.name = 'AbortError';
        throw abortError;
      }
      if (result.status === 'completed') {
        streamCompleted = true;
        onComplete(toCompletePayload(result.complete, fullResponse, turnId));
        return 'handled';
      }
      if (result.status === 'failed') {
        if (result.retryable && retryCount < MAX_AUTO_RETRY) {
          retryCount++;
          onChunk({ type: 'status', statusType: 'retrying', message: '잠시만요, 다시 시도하고 있어요...' });
          fullResponse = '';
          await new Promise(resolve => setTimeout(resolve, 1500));
          await attemptStream();
          return 'handled';
        }
        if (result.partial_text && !fullResponse) fullResponse = result.partial_text;
        onError(withPartial(chatStreamError(
          result.error || '답변을 만들지 못했어요. 다시 보내주세요.',
          result.error_code || 'unknown',
          { retryable: result.retryable === true, serverMessage: result.error || '' },
        )));
        return 'handled';
      }
      return result.status === 'timeout' ? 'timeout' : 'unknown';
    };

    try {
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'text/plain; charset=utf-8',
        'Cache-Control': 'no-cache'
      };
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }
      let response;
      requestSent = true;
      try {
        response = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify(turnId ? { ...requestData, client_turn_id: turnId } : requestData),
          credentials: 'omit',
          mode: 'cors',
          signal: signal
        });
      } catch (fetchError) {
        if (fetchError?.name === 'AbortError' || signal?.aborted) throw fetchError;
        throw chatStreamError(
          '인터넷 연결이 불안정해 메시지를 보내지 못했어요. 연결을 확인하고 다시 보내주세요.',
          'network',
          { retryable: true, serverMessage: String(fetchError?.message || '') },
        );
      }

      if (!response.ok) {
        throw await chatHttpError(response);
      }
      if (!response.body) {
        throw chatStreamError('답변을 받지 못했어요. 다시 보내주세요.', 'stream_unavailable', { retryable: true });
      }
      responseReceived = true;

      const mapReadError = (readError) => {
        if (readError?.name === 'AbortError' || signal?.aborted) return readError;
        return chatStreamError(
          '연결이 끊겨 답변을 끝까지 받지 못했어요. 다시 보내주세요.',
          'stream_network_lost',
          { retryable: true, serverMessage: String(readError?.message || '') },
        );
      };

      for await (const data of readSseJson(response.body, { mapReadError })) {
        try {
          if (data.type === 'heartbeat') {
            continue;
          }
          if (data.type === 'start') {
            sessionId = data.session_id;
            onChunk({ type: 'start', sessionId: sessionId });

          } else if (data.type === 'status') {
            onChunk({
              type: 'status',
              statusType: data.status_type,
              message: data.message,
              icon: data.icon,
              detail: data.detail,
              // attachment_missing: 첨부를 전제로 한 메시지인데 파일 본문이 안 갔을 때의 원인
              ...(typeof data.reason === 'string' ? { reason: data.reason } : {}),
              // file_context_truncated: 한도 때문에 온전히 들어가지 못한 첨부 파일 id 목록(일부만 포함 + 본문 생략)
              ...(Array.isArray(data.truncated_file_ids)
                ? { truncatedFileIds: data.truncated_file_ids }
                : {}),
              // 그중 본문이 통째로 생략된 파일 (이번 답변에 참고되지 않음)
              ...(Array.isArray(data.skipped_file_ids)
                ? { skippedFileIds: data.skipped_file_ids }
                : {}),
              ...(Array.isArray(data.skipped_file_names)
                ? { skippedFileNames: data.skipped_file_names }
                : {}),
              // quiet: 답이 끝난 뒤 칩 라벨만 바꾸는 안내(AI 가 필요한 파일은 직접 읽었음) — 토스트·진행 문구 없음
              ...(data.quiet === true ? { quiet: true } : {}),
              // 그중 AI 가 필요하면 더 읽을 수 있었던 파일 — 칩에 '필요한 부분만 읽음'
              ...(Array.isArray(data.readable_file_ids)
                ? { readableFileIds: data.readable_file_ids }
                : {}),
              // attachment_dropped: 삭제됐거나 접근할 수 없어 서버가 뺀 첨부·수정 대상 파일
              ...(Array.isArray(data.dropped_file_ids)
                ? { droppedFileIds: data.dropped_file_ids, droppedReasons: data.reasons || [] }
                : {}),
              // file_artifact_planned / file_artifact_generating: 만들 예정인 파일 형식·이름
              ...(Array.isArray(data.artifacts_planned)
                ? { artifactsPlanned: data.artifacts_planned }
                : {}),
            });

          } else if (data.type === 'content') {
            const chunk = data.chunk || '';
            fullResponse = data.full_content || fullResponse + chunk;
            onChunk({ type: 'content', chunk: chunk, fullContent: fullResponse });

          } else if (data.type === 'complete') {
            streamCompleted = true;
            fullResponse = data.full_response || fullResponse;
            onComplete(toCompletePayload(data, fullResponse, turnId));
            return;

          } else if (data.type === 'error') {
            streamErrored = true;
            const isRetryable = data.retryable === true;
            const errorCode = data.error_code || 'unknown';

            if (isRetryable && retryCount < MAX_AUTO_RETRY) {
              retryCount++;
              onChunk({ type: 'status', statusType: 'retrying', message: '잠시만요, 다시 시도하고 있어요...' });
              fullResponse = '';
              await new Promise(resolve => setTimeout(resolve, 1500));
              return attemptStream();
            }

            const errorObj = new Error(data.error);
            errorObj.errorInfo = { code: errorCode, retryable: isRetryable, serverMessage: data.error };
            onError(withPartial(errorObj));
            return;

          } else {
            // 🆕 알려지지 않은 청크 타입은 그대로 통과시킨다.
            //   (예: hwpx_edit_result — 양식 in-place 채움 결과 → 미리보기 즉시 갱신)
            //   백엔드가 새 청크를 추가해도 프론트는 onChunk로 그대로 받아 처리한다.
            onChunk(data);
          }

        } catch (handleError) {
          // 청크 하나를 처리하다 난 오류는 무시하고 다음 청크를 읽는다
        }
      }

      if (!streamCompleted && !streamErrored) {
        const recovery = await recoverTurn();
        if (recovery === 'handled') return;
        const errorObj = new Error('연결이 예기치 않게 종료됐어요. 다시 시도해주세요.');
        errorObj.errorInfo = { code: 'stream_interrupted', retryable: true, serverMessage: '' };

        if (recovery === 'unknown' && retryCount < MAX_AUTO_RETRY) {
          retryCount++;
          onChunk({ type: 'status', statusType: 'retrying', message: '잠시만요, 다시 시도하고 있어요...' });
          fullResponse = '';
          await new Promise(resolve => setTimeout(resolve, 1500));
          return attemptStream();
        }

        onError(withPartial(errorObj));
      }

    } catch (caughtError) {
      let error = caughtError;
      // 'network': 첫 응답 전에 끊겼다 — 요청이 서버에 닿아 답을 만들고 있을 수 있다
      if (['stream_network_lost', 'network'].includes(error?.errorInfo?.code) && !signal?.aborted) {
        try {
          if ((await recoverTurn()) === 'handled') return;
        } catch (recoverError) {
          error = recoverError;
        }
      }
      if (error?.name === 'AbortError' || signal?.aborted) {
        const keepPartial = Boolean(keepPartialOnStop && turnId && keepablePartialText(fullResponse));
        cancelChatTurn({ baseUrl: API_BASE_URL, turnId, getToken: getFreshToken, keepPartial });
        if (onComplete) {
          onComplete({
            sessionId: sessionId,
            turnId,
            fullResponse: fullResponse,
            message: null,
            timestamp: new Date().toISOString(),
            aborted: true,
            keepPartial,
          });
        }
        return;
      }

      console.error('❌ AI 상담 스트리밍 오류:', error);

      if (!error.errorInfo) {
        error.errorInfo = { code: getTrackingErrorCode(error), retryable: false, serverMessage: error.message };
      }
      onError(withPartial(error));
    }
  };

  await attemptStream();
};

/**
 * 로그인 사용자가 소유한 채팅 생성 파일을 비공개 API에서 내려받는다.
 * Storage URL을 메시지에 저장하지 않아 대화 공유/ID 유출로 파일이 노출되지 않는다.
 */
export const fetchChatArtifact = async (artifact) => {
  const fileId = String(artifact?.file_id || '').replace(/-/g, '').toLowerCase();
  if (!/^[0-9a-f]{32}$/.test(fileId)) {
    throw new Error('파일 정보를 확인하지 못했어요.');
  }
  const currentUser = getAuth().currentUser;
  if (!currentUser) {
    throw new Error('파일을 받으려면 로그인이 필요해요.');
  }

  const endpoint = `${API_BASE_URL}/api/chat/artifacts/${fileId}/download`;
  const requestWithToken = async (token) => fetchWithRetry(
    endpoint,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'omit',
      mode: 'cors',
    },
    {
      maxRetries: 1,
      timeoutMs: 120_000,
      retryOnTimeout: true,
    }
  );

  let response = await requestWithToken(await currentUser.getIdToken());
  if (response.status === 401) {
    response = await requestWithToken(await currentUser.getIdToken(true));
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || '파일을 내려받지 못했어요. 다시 시도해주세요.');
  }

  const disposition = response.headers.get('content-disposition') || '';
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  let filename = artifact?.filename || 'whatquiz-file';
  if (encodedName) {
    try {
      filename = decodeURIComponent(encodedName);
    } catch (_) {
      // The persisted artifact filename remains a safe fallback.
    }
  }
  const expectedLibraryContentId = `artifact_${fileId}`;
  const legacyLibraryContentId = (
    artifact?.library_content_id === expectedLibraryContentId
      ? expectedLibraryContentId
      : null
  );
  const responseLibraryContentId = (
    response.headers.get('x-library-content-id') || ''
  ).trim();
  const responseConfirmsLibrarySave = (
    response.headers.get('x-library-saved') || ''
  ).toLowerCase() === 'true';
  const confirmedLibraryContentId = (
    responseConfirmsLibrarySave
    && responseLibraryContentId === expectedLibraryContentId
      ? expectedLibraryContentId
      : null
  );
  return {
    blob: await response.blob(),
    filename,
    // Legacy artifacts already carried this ID at generation time. New
    // artifacts trust only the explicit download-save contract header.
    library_content_id: legacyLibraryContentId || confirmedLibraryContentId,
  };
};

/**
 * 다운로드와 무관하게 현재 artifact가 라이브러리에 등록됐는지 확인한다.
 * 구 backend의 404는 rolling deploy 호환을 위해 "미지원"으로 조용히 처리한다.
 */
export const fetchChatArtifactLibraryStatus = async (artifact) => {
  const fileId = String(artifact?.file_id || '').replace(/-/g, '').toLowerCase();
  if (!/^[0-9a-f]{32}$/.test(fileId)) {
    return {
      supported: false,
      saved: false,
      library_content_id: null,
    };
  }
  const currentUser = getAuth().currentUser;
  if (!currentUser) {
    return {
      supported: false,
      saved: false,
      library_content_id: null,
    };
  }

  const endpoint = (
    `${API_BASE_URL}/api/chat/artifacts/${fileId}/library-status`
  );
  const requestWithToken = async (token) => fetchWithRetry(
    endpoint,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'omit',
      mode: 'cors',
    },
    {
      maxRetries: 1,
      timeoutMs: 15_000,
      retryOnTimeout: true,
    }
  );

  let response = await requestWithToken(await currentUser.getIdToken());
  if (response.status === 401) {
    response = await requestWithToken(await currentUser.getIdToken(true));
  }
  if (response.status === 404) {
    return {
      supported: false,
      saved: false,
      library_content_id: null,
    };
  }
  if (!response.ok) {
    throw new Error('라이브러리 저장 상태를 확인하지 못했어요.');
  }

  const body = await response.json();
  const expectedLibraryContentId = `artifact_${fileId}`;
  const saved = (
    body?.saved === true
    && body?.file_id === fileId
    && body?.library_content_id === expectedLibraryContentId
  );
  return {
    supported: true,
    saved,
    library_content_id: saved ? expectedLibraryContentId : null,
  };
};

// ========================================
// 🗂️ 채팅 파일 첨부 API
// ========================================

// 이보다 작은 PDF는 다시 올려도 금방이라 확인하지 않는다(쪽 저장본도 큰 문서만 만든다).
export const KNOWN_PDF_MIN_BYTES = 2 * 1024 * 1024;

const sha256Hex = async (file) => {
  const digest = await window.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * 전에 정리한 같은 PDF면 업로드 없이 바로 첨부한다. 못 붙이면 null → 평소 업로드.
 */
export const attachKnownPdf = async (file, sessionId, authToken) => {
  const isPdf = file?.type === 'application/pdf' || /\.pdf$/i.test(file?.name || '');
  if (!isPdf || !authToken || (file.size || 0) < KNOWN_PDF_MIN_BYTES || !window.crypto?.subtle) {
    return null;
  }
  try {
    const sha256 = await sha256Hex(file);
    const response = await fetch(`${API_BASE_URL}/api/chat/files/attach-known`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ session_id: sessionId, sha256, filename: file.name }),
    });
    if (!response.ok) return null;
    const body = await response.json().catch(() => null);
    return body?.reused && body.file?.file_id ? { ...body.file, reused: true } : null;
  } catch {
    return null;
  }
};

/**
 * 채팅용 파일 업로드
 * @param {File} file - 업로드할 파일
 * @param {string} sessionId - 채팅 세션 ID
 * @param {string} userId - 사용자 ID (선택사항)
 * @param {function} onProgress - 진행률 콜백 (선택사항)
 * @param {string} attachmentPurpose - 파일 용도
 * ('content_reference' | 'design_reference' | 'assessment_plan_source')
 * @returns {Promise<Object>} 업로드 결과
 */
export const uploadChatFile = async (
  file,
  sessionId,
  userId = null,
  onProgress = null,
  attachmentPurpose = 'content_reference'
) => {
  try {
    const endpoint = `${API_BASE_URL}/api/chat/files/upload`;
    const currentUser = getAuth().currentUser;
    const authToken = currentUser ? await currentUser.getIdToken() : null;

    if (currentUser && userId && attachmentPurpose === 'content_reference') {
      const known = await attachKnownPdf(file, sessionId, authToken);
      if (known) {
        if (typeof onProgress === 'function') onProgress(100);
        return known;
      }
    }

    const formData = new FormData();
    if (currentUser && userId) {
      const descriptor = await uploadChatAttachment(
        file,
        currentUser.uid,
        onProgress
      );
      formData.append('file_descriptor', JSON.stringify(descriptor));
    } else {
      // 비인증 레거시 호출은 기존 multipart 계약을 유지한다.
      formData.append('file', file);
    }
    formData.append('session_id', sessionId);
    formData.append('attachment_purpose', attachmentPurpose);
    if (userId) {
      formData.append('user_id', userId);
    }

    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }
    const response = await fetchWithRetry(
      endpoint,
      {
        method: 'POST',
        headers,
        body: formData,
      },
      {
        maxRetries: 1,
        timeoutMs: 600_000,
        retryOnTimeout: false,
      }
    );
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      const httpError = new Error(body?.detail || '파일 업로드에 실패했습니다.');
      httpError.status = response.status;
      throw httpError;
    }
    if (typeof onProgress === 'function') {
      onProgress(100);
    }
    return body;
  } catch (error) {
    console.error('❌ 파일 업로드 오류:', error);
    const wrapped = new Error(error?.message || '파일 업로드 중 오류가 발생했습니다.');
    if (error?.status) wrapped.status = error.status;
    throw wrapped;
  }
};

// 📺 유튜브 영상 읽기 (SSE). 서버가 Gemini로 영상을 직접 보고 노트를 만들어 첨부 파일 레코드로 저장한다.
//   - 로그인 필수. 진행 이벤트: checking / cache_hit / reading, 연결 유지용 ping(10초).
//   - 성공 시 업로드 응답과 같은 모양의 file 객체를 반환, 실패 시 Error(code, retryable, message).
const YOUTUBE_INGEST_INACTIVITY_MS = 60_000; // 서버 ping 10초 간격 → 60초 무응답이면 끊긴 것으로 본다
const YOUTUBE_INGEST_TOTAL_MS = 15 * 60_000; // Gemini 타임아웃(10분) + 재시도 여유

export const ingestYouTubeVideo = async (url, sessionId, { onProgress = null, signal = null } = {}) => {
  const currentUser = getAuth().currentUser;
  if (!currentUser) {
    const error = new Error('유튜브 영상 읽기는 로그인 후 사용할 수 있어요.');
    error.code = 'login_required';
    throw error;
  }
  const authToken = await currentUser.getIdToken();
  const controller = new AbortController();
  const onExternalAbort = () => controller.abort();
  if (signal) signal.addEventListener('abort', onExternalAbort, { once: true });
  const totalTimer = setTimeout(() => controller.abort(), YOUTUBE_INGEST_TOTAL_MS);

  const makeError = (message, code = 'model_error', retryable = false) => {
    const error = new Error(message || '영상을 읽는 중 오류가 났어요. 잠시 후 다시 시도해 주세요.');
    error.code = code;
    error.retryable = retryable;
    return error;
  };

  try {
    const response = await fetch(`${API_BASE_URL}/api/chat/youtube/ingest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({ url, session_id: sessionId || null }),
      signal: controller.signal,
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      if (response.status === 401 || response.status === 403) {
        throw makeError('유튜브 영상 읽기는 로그인 후 사용할 수 있어요.', 'login_required');
      }
      throw makeError(body?.detail || `영상 읽기 요청에 실패했어요 (${response.status})`, 'model_error', true);
    }

    let result = null;
    let failure = null;
    const events = readSseJson(response.body, {
      idleTimeoutMs: YOUTUBE_INGEST_INACTIVITY_MS,
      idleTimeoutError: () => makeError('서버 응답이 없어요. 잠시 후 다시 시도해 주세요.', 'timeout', true),
    });
    for await (const event of events) {
      if (event.type === 'progress') {
        if (onProgress) onProgress(event);
      } else if (event.type === 'done') {
        result = event.file;
      } else if (event.type === 'error') {
        failure = makeError(event.message, event.code, !!event.retryable);
      }
      if (result || failure) break;
    }
    if (failure) throw failure;
    if (!result) throw makeError('영상 읽기가 중간에 끊겼어요. 다시 시도해 주세요.', 'model_error', true);
    return result;
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw makeError('영상 읽기가 너무 오래 걸려 중단했어요. 잠시 후 다시 시도해 주세요.', 'timeout', true);
    }
    throw error;
  } finally {
    clearTimeout(totalTimer);
    if (signal) signal.removeEventListener('abort', onExternalAbort);
  }
};

// 🔗 외부 링크 본문 읽기 (SSE, 한 번에 최대 10개, 링크별 독립 결과)
//   onEvent(event): {type:'progress'|'link_done'|'link_error', index, url, ...}
//   반환: { files: [{index, url, file}], errors: [{index, url, code, message, retryable}] }
const WEB_LINK_INGEST_INACTIVITY_MS = 60_000;
const WEB_LINK_INGEST_TOTAL_MS = 6 * 60_000; // 링크 1개 최대 ~90초 × 병렬 5 → 10개도 3분 안쪽, 여유 포함

export const ingestWebLinks = async (urls, sessionId, { onEvent = null, signal = null } = {}) => {
  const currentUser = getAuth().currentUser;
  if (!currentUser) {
    const error = new Error('링크 읽기는 로그인 후 사용할 수 있어요.');
    error.code = 'login_required';
    throw error;
  }
  const authToken = await currentUser.getIdToken();
  const controller = new AbortController();
  const onExternalAbort = () => controller.abort();
  if (signal) signal.addEventListener('abort', onExternalAbort, { once: true });
  const totalTimer = setTimeout(() => controller.abort(), WEB_LINK_INGEST_TOTAL_MS);

  const makeError = (message, code = 'model_error', retryable = false) => {
    const error = new Error(message || '링크를 읽는 중 오류가 났어요. 잠시 후 다시 시도해 주세요.');
    error.code = code;
    error.retryable = retryable;
    return error;
  };

  const files = [];
  const errors = [];
  try {
    const response = await fetch(`${API_BASE_URL}/api/chat/links/ingest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({ urls, session_id: sessionId || null }),
      signal: controller.signal,
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      if (response.status === 401 || response.status === 403) {
        throw makeError('링크 읽기는 로그인 후 사용할 수 있어요.', 'login_required');
      }
      throw makeError(body?.detail || `링크 읽기 요청에 실패했어요 (${response.status})`, 'model_error', true);
    }

    let finished = false;
    const events = readSseJson(response.body, {
      idleTimeoutMs: WEB_LINK_INGEST_INACTIVITY_MS,
      idleTimeoutError: () => makeError('서버 응답이 없어요. 잠시 후 다시 시도해 주세요.', 'timeout', true),
    });
    for await (const event of events) {
      if (event.type === 'ping') continue;
      if (event.type === 'link_done') {
        files.push({ index: event.index, url: event.url, file: event.file });
      } else if (event.type === 'link_error') {
        errors.push({ index: event.index, url: event.url, code: event.code, message: event.message, retryable: !!event.retryable });
      } else if (event.type === 'done') {
        finished = true;
      }
      if (onEvent) onEvent(event);
      if (finished) break;
    }
    if (!finished && files.length === 0) {
      throw makeError('링크 읽기가 중간에 끊겼어요. 다시 시도해 주세요.', 'model_error', true);
    }
    return { files, errors };
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw makeError('링크 읽기가 너무 오래 걸려 중단했어요. 잠시 후 다시 시도해 주세요.', 'timeout', true);
    }
    throw error;
  } finally {
    clearTimeout(totalTimer);
    if (signal) signal.removeEventListener('abort', onExternalAbort);
  }
};

// 🎙️ 음성 입력: 녹음(최대 2분)을 보내 받아쓴 글을 받는다. 녹음은 서버에 저장되지 않는다.
//   실패 시 Error(code: login_required | daily_limit | unsupported_format | too_large | engine_error)
export const transcribeVoice = async (audioBlob, { signal = null } = {}) => {
  const currentUser = getAuth().currentUser;
  if (!currentUser) {
    const error = new Error('음성 입력은 로그인 후 사용할 수 있어요.');
    error.code = 'login_required';
    throw error;
  }
  const authToken = await currentUser.getIdToken();
  const mime = (audioBlob.type || 'audio/webm').split(';')[0];
  const ext = mime.includes('mp4') ? 'mp4' : (mime.split('/')[1] || 'webm');
  const formData = new FormData();
  formData.append('audio', audioBlob, `voice.${ext}`);

  const response = await fetchWithRetry(
    `${API_BASE_URL}/api/chat/voice/transcribe`,
    {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${authToken}` },
      body: formData,
      signal,
    },
    {
      maxRetries: 1,
      timeoutMs: calcApiTimeout('voice-transcribe'),
      retryOnTimeout: false,
    }
  );
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const code = response.status === 401 || response.status === 403
      ? 'login_required'
      : (body?.code || 'engine_error');
    const error = new Error(body?.message || '받아쓰기에 실패했어요. 다시 시도해 주세요.');
    error.code = code;
    error.status = response.status;
    throw error;
  }
  return {
    text: typeof body?.text === 'string' ? body.text : '',
    model: typeof body?.model === 'string' ? body.model : '',
    seconds: Number(body?.seconds) || 0,
    remainingSeconds: Number(body?.remaining_seconds) || 0,
  };
};

// 🔊 답변 듣기: 한 토막(최대 600자)을 AI 음성(mp3 스트림)으로 받는다. 응답 본문은 호출부가 읽는다.
export const requestSpeech = async (text, { signal = null } = {}) => {
  const currentUser = getAuth().currentUser;
  if (!currentUser) {
    const error = new Error('AI 음성 듣기는 로그인 후 사용할 수 있어요.');
    error.code = 'login_required';
    throw error;
  }
  const authToken = await currentUser.getIdToken();
  const response = await fetchWithRetry(
    `${API_BASE_URL}/api/chat/voice/speak`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({ text }),
      signal,
    },
    {
      maxRetries: 1,
      timeoutMs: calcApiTimeout('voice-speak'),
      retryOnTimeout: false,
    }
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const error = new Error(body?.message || 'AI 음성을 만들지 못했어요.');
    error.code = response.status === 401 || response.status === 403
      ? 'login_required'
      : (body?.code || 'engine_error');
    error.status = response.status;
    throw error;
  }
  return response;
};

export const getChatFilePreview = async (fileId) => {
  const currentUser = getAuth().currentUser;
  if (!currentUser) throw new Error('로그인이 필요합니다.');
  const authToken = await currentUser.getIdToken();
  const response = await fetchWithRetry(
    `${API_BASE_URL}/api/chat/files/${encodeURIComponent(fileId)}/preview`,
    {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    },
    {
      maxRetries: 1,
      timeoutMs: 90_000,
      retryOnTimeout: true,
    }
  );
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(body?.detail || '파일 미리보기를 불러오지 못했어요.');
  }
  return body;
};

export const downloadOriginalChatFile = async (fileId) => {
  const currentUser = getAuth().currentUser;
  if (!currentUser) throw new Error('로그인이 필요합니다.');
  const authToken = await currentUser.getIdToken();
  const response = await fetchWithRetry(
    `${API_BASE_URL}/api/chat/files/${encodeURIComponent(fileId)}/original-download`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    },
    {
      maxRetries: 1,
      timeoutMs: 90_000,
      retryOnTimeout: true,
    }
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || '원본 파일을 불러오지 못했어요.');
  }
  return response.blob();
};

export const downloadCurrentChatFile = async (fileId) => {
  const currentUser = getAuth().currentUser;
  if (!currentUser) throw new Error('로그인이 필요합니다.');
  const authToken = await currentUser.getIdToken();
  const response = await fetchWithRetry(
    `${API_BASE_URL}/api/chat/files/${encodeURIComponent(fileId)}/current-download`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    },
    {
      maxRetries: 1,
      timeoutMs: 90_000,
      retryOnTimeout: true,
    }
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.detail || 'AI 수정본을 불러오지 못했어요.');
  }
  return response.blob();
};

export const editChatOfficeFile = async (fileId, editRequest) => {
  const currentUser = getAuth().currentUser;
  if (!currentUser) throw new Error('로그인이 필요합니다.');
  const authToken = await currentUser.getIdToken();
  const response = await fetchWithRetry(
    `${API_BASE_URL}/api/chat/files/${encodeURIComponent(fileId)}/ai-edit`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(editRequest),
    },
    {
      maxRetries: 1,
      timeoutMs: 200_000,
      retryOnTimeout: true,
    }
  );
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(body?.detail || '파일을 수정하지 못했어요.');
    error.status = response.status;
    throw error;
  }
  return body;
};

export const getChatOfficeEditJob = async (jobId) => {
  const currentUser = getAuth().currentUser;
  if (!currentUser) throw new Error('로그인이 필요합니다.');
  const authToken = await currentUser.getIdToken();
  const response = await fetchWithRetry(
    `${API_BASE_URL}/api/chat/office-edit-jobs/${encodeURIComponent(jobId)}`,
    {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    },
    {
      maxRetries: 1,
      timeoutMs: 30_000,
      retryOnTimeout: true,
    }
  );
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(body?.detail || '파일 수정 상태를 확인하지 못했어요.');
  }
  return body;
};

/**
 * 채팅 세션의 업로드된 파일 목록 조회
 * @param {string} sessionId - 채팅 세션 ID
 * @returns {Promise<Object>} 파일 목록
 */
export const getChatFiles = async (sessionId) => {
  try {
    const endpoint = `${API_BASE_URL}/api/chat/files/${sessionId}`;
    const currentUser = getAuth().currentUser;
    const authToken = currentUser ? await currentUser.getIdToken() : null;
    if (!authToken) {
      throw new Error('로그인이 필요합니다.');
    }
    
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      credentials: 'omit',
      mode: 'cors'
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ 파일 목록 조회 오류:', response.status, errorText);
      throw new Error(`파일 목록을 가져오는 중 오류가 발생했습니다. 상태 코드: ${response.status}`);
    }
    
    const result = await response.json();
    //console.log('✅ 파일 목록 조회 성공:', result);
    return result;
    
  } catch (error) {
    console.error('❌ 파일 목록 조회 오류:', error);
    throw error;
  }
};

/**
 * 임시 업로드 세션의 파일들을 실제 채팅 세션으로 연결
 * @param {string} oldSessionId - 임시 업로드 세션 ID
 * @param {string} newSessionId - 실제 채팅 세션 ID
 * @returns {Promise<Object>} 업데이트 결과
 */
export const updateChatFilesSession = async (oldSessionId, newSessionId) => {
  try {
    const endpoint = `${API_BASE_URL}/api/chat/files/session`;
    const currentUser = getAuth().currentUser;
    const authToken = currentUser ? await currentUser.getIdToken() : null;
    if (!authToken) {
      throw new Error('로그인이 필요합니다.');
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      credentials: 'omit',
      mode: 'cors',
      body: JSON.stringify({
        old_session_id: oldSessionId,
        new_session_id: newSessionId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ 파일 세션 연결 오류:', response.status, errorText);
      throw new Error(`파일 세션 연결 중 오류가 발생했습니다. 상태 코드: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ 파일 세션 연결 오류:', error);
    throw error;
  }
};

/**
 * 채팅 파일 삭제
 * @param {string} fileId - 삭제할 파일 ID
 * @returns {Promise<Object>} 삭제 결과
 */
export const deleteChatFile = async (fileId) => {
  try {
    const endpoint = `${API_BASE_URL}/api/chat/files/${fileId}`;
    const currentUser = getAuth().currentUser;
    const authToken = currentUser ? await currentUser.getIdToken() : null;
    if (!authToken) {
      throw new Error('로그인이 필요합니다.');
    }
    
    const response = await fetch(endpoint, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      credentials: 'omit',
      mode: 'cors'
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ 파일 삭제 오류:', response.status, errorText);
      throw new Error(`파일을 삭제하는 중 오류가 발생했습니다. 상태 코드: ${response.status}`);
    }
    
    const result = await response.json();
    //console.log('✅ 파일 삭제 성공:', result);
    return result;
    
  } catch (error) {
    console.error('❌ 파일 삭제 오류:', error);
    throw error;
  }
};

/**
 * 지운 대화에만 쓰인 첨부(기록·원본·추출 본문)를 서버에서 정리한다.
 * @param {string} conversationId
 * @param {{session_ids: string[], file_ids: string[]}} refs
 */
export const cleanupConversationAttachments = async (conversationId, refs) => {
  const currentUser = getAuth().currentUser;
  const authToken = currentUser ? await currentUser.getIdToken() : null;
  if (!authToken) {
    throw new Error('로그인이 필요합니다.');
  }
  const response = await fetch(
    `${API_BASE_URL}/api/chat/conversations/${encodeURIComponent(conversationId)}/attachments/cleanup`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      credentials: 'omit',
      mode: 'cors',
      body: JSON.stringify(refs),
    }
  );
  if (!response.ok) {
    throw new Error(`첨부 정리 실패: ${response.status}`);
  }
  return response.json();
};

// ================================================================================
// 생기부 팩트체크 RAG 시스템 API
// ================================================================================

/**
 * 생기부 팩트체크 검색
 * @param {string} query - 검색 질문
 * @param {string|null} schoolLevel - 학교급 필터 (elementary, middle, high, null)
 * @param {number} topK - 검색 결과 개수
 * @param {number} minSimilarity - 최소 유사도 임계값
 * @returns {Promise<Object>} 검색 결과
 */
export const searchSaenggibuFactcheck = async (query, schoolLevel = null, topK = 5, minSimilarity = 0.1) => {
  try {
    //console.log('🔍 생기부 팩트체크 검색 요청:', { query, schoolLevel, topK, minSimilarity });
    
    const response = await fetch(`${API_BASE_URL}/api/saenggibu/factcheck`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'omit',
      mode: 'cors',
      body: JSON.stringify({
        query: query,
        school_level: schoolLevel,
        top_k: topK,
        min_similarity: minSimilarity,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '서버 오류가 발생했습니다.' }));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    //console.log('✅ 생기부 팩트체크 검색 완료:', data);
    return data;
    
  } catch (error) {
    console.error('❌ 생기부 팩트체크 검색 오류:', error);
    throw error;
  }
};

/**
 * 사용 가능한 학교급 목록 조회
 * @returns {Promise<Object>} 학교급 목록
 */
export const getSaenggibuSchoolLevels = async () => {
  try {
    //console.log('📋 생기부 학교급 목록 요청');
    
    const response = await fetch(`${API_BASE_URL}/api/saenggibu/school-levels`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'omit',
      mode: 'cors'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '서버 오류가 발생했습니다.' }));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    //console.log('✅ 생기부 학교급 목록 조회 완료:', data);
    return data;
    
  } catch (error) {
    console.error('❌ 생기부 학교급 목록 조회 오류:', error);
    throw error;
  }
};

/**
 * 생기부 RAG 시스템 상태 조회
 * @returns {Promise<Object>} 시스템 상태
 */
export const getSaenggibuSystemStatus = async () => {
  try {
    //console.log('📊 생기부 RAG 시스템 상태 요청');
    
    const response = await fetch(`${API_BASE_URL}/api/saenggibu/system-status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'omit',
      mode: 'cors'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '서버 오류가 발생했습니다.' }));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    //console.log('✅ 생기부 RAG 시스템 상태 조회 완료:', data);
    return data;
    
  } catch (error) {
    console.error('❌ 생기부 RAG 시스템 상태 조회 오류:', error);
    throw error;
  }
};

/**
 * 엑셀 파일을 업로드하고 AI로 구조 분석
 * @param {File} file - 업로드할 엑셀 파일
 * @param {string} authToken - 사용자 인증 토큰
 * @param {string} category - 생기부 카테고리
 * @param {Object} config - 요청 옵션
 * @param {AbortSignal} config.signal - 요청 취소 신호
 * @returns {Promise<Object>} 분석 결과
 */
export const analyzeExcelFile = async (file, authToken, category = '일괄생성', config = {}) => {
  try {
    const formData = new FormData();
    if (file?.storage_path) {
      formData.append('file_descriptor', JSON.stringify(file));
    } else {
      formData.append('file', file);
    }
    formData.append('category', category);

    const endpoint = getApiUrl('/api/saenggibu/analyze-excel');
    
    const response = await fetchWithRetry(
      endpoint,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'application/json'
        },
        body: formData,
        credentials: 'omit',
        mode: 'cors',
        signal: config.signal
      },
      {
        maxRetries: 1,
        timeoutMs: calcApiTimeout('student-split'),
        retryOnTimeout: false
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '서버 오류가 발생했습니다.' }));
      // Error 객체에 response 정보를 추가하여 던지기
      const error = new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      error.response = {
        status: response.status,
        data: errorData
      };
      throw error;
    }

    const data = await response.json();
    return data;
    
  } catch (error) {
    console.error('엑셀 분석 오류:', error);
    throw error;
  }
};

/**
 * 붙여넣은 표/텍스트를 AI로 분석해 일괄 생성용 학생 정보를 추출
 * @param {string} text - 붙여넣은 표 또는 텍스트
 * @param {string} category - 생기부 카테고리
 * @param {string} authToken - 사용자 인증 토큰
 * @returns {Promise<Object>} 분석 결과
 */
export const analyzePastedBatchText = async (text, category, authToken) => {
  try {
    const endpoint = getApiUrl('/api/saenggibu/analyze-pasted-text');
    const headers = getApiHeaders(authToken);

    const response = await fetchWithRetry(
      endpoint,
      {
        method: 'POST',
        headers: { ...headers, 'Accept': 'application/json' },
        body: JSON.stringify({ text, category }),
        credentials: 'omit',
        mode: 'cors',
      },
      { maxRetries: 1, timeoutMs: calcApiTimeout('batch-saenggibu', { studentCount: 10 }) }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '서버 오류가 발생했습니다.' }));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('붙여넣기 분석 오류:', error);
    throw error;
  }
};

/**
 * 일괄 생기부 생성 요청 (비스트리밍 - 안정적)
 * @param {Array} studentsData - 학생 데이터 배열
 * @param {Object} options - 생성 옵션
 * @param {string} authToken - 사용자 인증 토큰
 * @param {Object} metadata - 파일 분석 메타데이터
 * @param {Object} config - 요청 옵션
 * @param {AbortSignal} config.signal - 요청 취소 신호
 * @returns {Promise<Object>} 생성 결과
 */
export const generateBatchSaenggibu = async (studentsData, options, authToken, metadata = {}, config = {}) => {
  try {
    const endpoint = getApiUrl('/api/saenggibu/batch-generate');
    const headers = getApiHeaders(authToken);
    const studentCount = Array.isArray(studentsData) ? studentsData.length : 1;
    const timeoutMs = calcApiTimeout('batch-saenggibu', { studentCount });

    const response = await fetchWithRetry(
      endpoint,
      {
        method: 'POST',
        headers: { ...headers, 'Accept': 'application/json' },
        body: JSON.stringify({ students_data: studentsData, options: options, metadata }),
        credentials: 'omit',
        mode: 'cors',
        signal: config.signal
      },
      { maxRetries: 2, timeoutMs, retryOnTimeout: false }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '서버 오류가 발생했습니다.' }));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
    
  } catch (error) {
    console.error('❌ 일괄 생기부 생성 오류:', error);
    throw error;
  }
};

// ========================================
// 일괄 생기부 스트리밍 생성 (하트비트 + 멱등 재접속)
// ========================================
// 배경: 일부 학교/기관 네트워크 장비는 약 3분간 데이터가 흐르지 않는 연결을
// 끊는다. 비스트리밍 일괄 생성은 3~4분간 침묵해서 여기에 걸렸고,
// 자동 재전송이 같은 배치를 중복 생성했다 (2026-07 사건).
// 새 스트림 엔드포인트는 서버가 10초마다 진행 이벤트를 보내 끊김을 막고,
// 같은 generation_job_id 재접속은 서버 멱등성 가드가 실행 중인 작업에
// 합류시키거나 저장된 결과를 즉시 돌려준다 (중복 생성 없음).

// 서버 heartbeat 이 10초 간격이므로 75초 침묵 = 죽은 연결로 판단
const BATCH_STREAM_INACTIVITY_TIMEOUT_MS = 75_000;
// 연결(재접속 포함) 최대 시도 횟수
const BATCH_STREAM_MAX_CONNECTION_ATTEMPTS = 5;
const BATCH_STREAM_RECONNECT_DELAY_MS = 2_000;

const isAbortError = (error) => error?.name === 'AbortError';

const isNetworkishBatchError = (error) => {
  const message = String(error?.message || '');
  return (
    message.includes('Failed to fetch') ||
    message.includes('NetworkError') ||
    message.includes('Load failed') ||
    message.includes('BATCH_STREAM_INACTIVITY_TIMEOUT') ||
    message.includes('BATCH_STREAM_ENDED_WITHOUT_COMPLETE') ||
    message.includes('network error')
  );
};

/**
 * 일괄 생성 오류를 사용자에게 그대로 보여줄 수 있는 문구로 변환.
 * 원문 네트워크 에러("Failed to fetch" 등)를 사용자에게 노출하지 않는다.
 */
export const getFriendlyBatchErrorMessage = (error) => {
  if (error?.isServerReportedError || error?.isHttpError) {
    return error.message || '생성 중 오류가 발생했어요. 잠시 후 다시 시도해주세요.';
  }
  if (isNetworkishBatchError(error)) {
    return '네트워크 연결이 불안정해요. 생성이 완료되었다면 생기부 기록에 자동 저장되니 잠시 후 확인해주세요.';
  }
  return error?.message || '생성 중 오류가 발생했어요. 잠시 후 다시 시도해주세요.';
};

/** 스트림 응답 본문을 읽어 progress 콜백을 호출하고 complete 페이로드를 반환 */
const readBatchGenerationStream = async (response, { onProgress } = {}) => {
  const events = readSseJson(response.body, {
    idleTimeoutMs: BATCH_STREAM_INACTIVITY_TIMEOUT_MS,
    idleTimeoutError: () => new Error('BATCH_STREAM_INACTIVITY_TIMEOUT'),
    cancelOnExit: true,
  });
  for await (const parsed of events) {
    if (parsed.type === 'start' || parsed.type === 'progress') {
      onProgress?.(parsed);
    } else if (parsed.type === 'complete') {
      return parsed;
    } else if (parsed.type === 'cancelled') {
      return {
        success: false,
        cancelled: true,
        data: { cancelled: true },
        message: parsed.message || '생성을 중단했어요.',
      };
    } else if (parsed.type === 'error') {
      const serverError = new Error(parsed.message || '생성 중 오류가 발생했어요.');
      // 서버가 판단한 실패 → 재접속해도 같은 결과이므로 즉시 실패 처리
      serverError.isServerReportedError = true;
      throw serverError;
    }
  }
  // complete 없이 스트림이 끝남 → 연결 유실로 간주해 재접속 유도
  throw new Error('BATCH_STREAM_ENDED_WITHOUT_COMPLETE');
};

/**
 * 일괄 생기부 생성 (하트비트 스트리밍 + 자동 재접속)
 * @param {Array} studentsData - 학생 데이터 배열
 * @param {Object} options - 생성 옵션
 * @param {Function} getAuthToken - async () => 인증 토큰 (재접속 시 갱신)
 * @param {Object} metadata - 파일 분석 메타데이터 (generation_job_id 포함 권장)
 * @param {Object} config - { signal, onProgress }
 * @returns {Promise<Object>} 생성 결과 (legacy batch-generate 와 같은 형태)
 */
export const generateBatchSaenggibuStream = async (
  studentsData,
  options,
  getAuthToken,
  metadata = {},
  config = {}
) => {
  const endpoint = getApiUrl('/api/saenggibu/batch-generate-stream');
  const { signal, onProgress } = config;
  const body = JSON.stringify({ students_data: studentsData, options, metadata });

  let lastError = null;
  for (let attempt = 1; attempt <= BATCH_STREAM_MAX_CONNECTION_ATTEMPTS; attempt++) {
    if (signal?.aborted) {
      const abortError = new Error('생성을 중단했어요.');
      abortError.name = 'AbortError';
      throw abortError;
    }
    if (attempt > 1) {
      onProgress?.({ type: 'progress', stage: 'reconnecting', attempt });
      await new Promise(resolve => setTimeout(resolve, BATCH_STREAM_RECONNECT_DELAY_MS));
    }

    try {
      const authToken = await getAuthToken();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { ...getApiHeaders(authToken), 'Accept': 'text/event-stream' },
        body,
        credentials: 'omit',
        mode: 'cors',
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const httpError = new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        httpError.isHttpError = true;
        httpError.status = response.status;
        // 4xx(인증/권한/요청 오류)는 재접속해도 같은 결과 → 즉시 실패
        if (response.status >= 400 && response.status < 500) {
          throw httpError;
        }
        lastError = httpError;
        continue;
      }

      return await readBatchGenerationStream(response, { onProgress });
    } catch (error) {
      if (signal?.aborted || isAbortError(error)) {
        throw error;
      }
      if (error.isServerReportedError || (error.isHttpError && error.status < 500)) {
        throw error;
      }
      // 네트워크성 오류/침묵 타임아웃 → 같은 jobId 로 재접속
      // (서버 멱등성 가드가 실행 중 작업 합류/저장된 결과 재사용을 보장)
      console.warn(`일괄 생성 스트림 연결 유실 (시도 ${attempt}/${BATCH_STREAM_MAX_CONNECTION_ATTEMPTS}):`, error?.message);
      lastError = error;
    }
  }
  throw lastError || new Error('네트워크 연결이 불안정해요.');
};

/**
 * 일괄 생기부 전체 재생성 요청
 * 하트비트 스트림 경로를 재사용하되, 재생성 전용 jobId 와 메타데이터를 전달한다.
 */
export const regenerateBatchSaenggibu = async ({
  studentsData,
  options = {},
  authToken,
  metadata = {},
  regenerationInstruction = '',
  regeneratedFromRecordId = null,
  onRetry,
}) => {
  try {
    const regeneratedOptions = {
      ...options,
      regenerationInstruction,
    };
    const regenerationJobId = `batchregen_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const regeneratedMetadata = {
      ...metadata,
      is_regeneration: true,
      regenerated_from_record_id: regeneratedFromRecordId,
      regenerated_at: new Date().toISOString(),
      generation_job_id: regenerationJobId,
    };

    return await generateBatchSaenggibuStream(
      studentsData,
      regeneratedOptions,
      async () => authToken,
      regeneratedMetadata,
      {
        onProgress: (event) => {
          if (event?.stage === 'reconnecting' && typeof onRetry === 'function') {
            onRetry(event.attempt || 1);
          }
        },
      }
    );
  } catch (error) {
    console.error('❌ 일괄 생기부 전체 재생성 오류:', error);
    throw error;
  }
};

// ========================================
// 🔗 대화 공유 API
// ========================================

/**
 * 대화 공유 링크 생성
 * @param {Object} shareData - 공유할 대화 데이터
 * @param {string} shareData.conversation_id - 원본 대화 ID (선택)
 * @param {Array} shareData.messages - 공유할 메시지 목록
 * @param {string} shareData.category - 대화 카테고리 (선택)
 * @param {string} shareData.category_name - 카테고리 이름 (선택)
 * @param {string} shareData.title - 공유 제목 (선택, 자동 생성)
 * @returns {Promise<Object>} 공유 결과 { success, share_id, share_url }
 */
export const createShareLink = async (shareData) => {
  try {
    const endpoint = `${API_BASE_URL}/api/chat/share`;
    const currentUser = getAuth().currentUser;
    const idToken = currentUser
      ? await currentUser.getIdToken()
      : null;
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(idToken && { 'Authorization': `Bearer ${idToken}` })
      },
      body: JSON.stringify(shareData),
      credentials: 'omit',
      mode: 'cors'
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ 공유 링크 생성 오류:', response.status, errorText);
      throw new Error(`공유 링크를 생성하는 중 오류가 발생했습니다.`);
    }
    
    const result = await response.json();
    return result;
    
  } catch (error) {
    console.error('❌ 공유 링크 생성 오류:', error);
    throw error;
  }
};

/**
 * 공유된 대화 조회
 * @param {string} shareId - 공유 ID
 * @returns {Promise<Object>} 공유된 대화 데이터
 */
export const getSharedConversation = async (shareId) => {
  try {
    const endpoint = `${API_BASE_URL}/api/chat/share/${shareId}`;
    
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'omit',
      mode: 'cors'
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('공유된 대화를 찾을 수 없습니다.');
      }
      const errorText = await response.text();
      console.error('❌ 공유 대화 조회 오류:', response.status, errorText);
      throw new Error(`공유된 대화를 불러오는 중 오류가 발생했습니다.`);
    }
    
    const result = await response.json();
    return result;
    
  } catch (error) {
    console.error('❌ 공유 대화 조회 오류:', error);
    throw error;
  }
};

/**
 * 공유 링크 삭제 (비활성화)
 * @param {string} shareId - 공유 ID
 * @returns {Promise<Object>} 삭제 결과
 */
export const deleteShareLink = async (shareId) => {
  try {
    const endpoint = `${API_BASE_URL}/api/chat/share/${shareId}`;
    
    const response = await fetch(endpoint, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'omit',
      mode: 'cors'
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ 공유 링크 삭제 오류:', response.status, errorText);
      throw new Error(`공유 링크를 삭제하는 중 오류가 발생했습니다.`);
    }
    
    const result = await response.json();
    return result;
    
  } catch (error) {
    console.error('❌ 공유 링크 삭제 오류:', error);
    throw error;
  }
};

// ========================================
// 🔍 파일 사전 검사 API
// ========================================

/**
 * 파일 업로드 전 사전 검사 (실제 파일 전송)
 * 파일 분석 제한에 대해 사용자에게 투명하게 안내
 * PDF의 경우 실제 페이지 수를 확인하여 정확한 제한 체크
 * @param {Array} files - 검사할 파일 배열 (File 객체들)
 * @param {string} feature - 기능 구분 ("chat" | "saenggibu" | "batch" | "inspector" | "lab")
 * @param {boolean} [isPro=false] - Pro 사용자 여부. true 면 Pro 한도(200k자/30MB) 기준으로 안내한다.
 *   서버는 이 값을 안내 표기에만 사용하며, 실제 업로드는 Authorization 토큰으로 재검증한다.
 * @returns {Promise<Object>} 사전 검사 결과
 */
// 단일 청크에 대한 사전 검사 호출. preCheckFiles 내부에서 사용.
const _preCheckFilesSingle = async (files, feature, isPro = false) => {
  const endpoint = `${API_BASE_URL}/api/file/pre-check-upload`;
  const formData = new FormData();
  formData.append('feature', feature);
  formData.append('is_pro', isPro ? 'true' : 'false');
  files.forEach(file => {
    formData.append('files', file);
  });

  const response = await fetch(endpoint, {
    method: 'POST',
    credentials: 'omit',
    mode: 'cors',
    body: formData
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ 파일 사전 검사 오류:', response.status, errorText);
    return null;
  }
  return await response.json();
};

// 여러 청크의 사전 검사 결과를 단일 결과로 합친다.
const _mergePreCheckResults = (results, totalFiles, totalSize) => {
  if (!results || results.length === 0) return null;

  // overall_status 우선순위: error > warning > ok
  const statusRank = { error: 3, warning: 2, ok: 1 };
  let overallStatus = 'ok';
  for (const r of results) {
    if ((statusRank[r.overall_status] || 0) > (statusRank[overallStatus] || 0)) {
      overallStatus = r.overall_status;
    }
  }

  const merged = {
    can_proceed: results.every(r => r.can_proceed !== false),
    needs_confirmation: results.some(r => r.needs_confirmation === true),
    overall_status: overallStatus,
    title: results[0].title || '파일 분석 준비',
    message: results[0].message || '',
    total_files: totalFiles,
    total_size: totalSize,
    total_size_display: `${(totalSize / 1024 / 1024).toFixed(1)}MB`,
    files_info: results.flatMap(r => r.files_info || []),
    details: Array.from(new Set(results.flatMap(r => r.details || []))),
    recommendations: Array.from(new Set(results.flatMap(r => r.recommendations || []))),
    limits_info: results[0].limits_info || {},
  };

  // exceeded_limits가 있으면 머지
  const allExceeded = results.flatMap(r => r.exceeded_limits || []);
  if (allExceeded.length > 0) {
    merged.exceeded_limits = Array.from(new Set(allExceeded));
  }
  return merged;
};

/**
 * 파일 사전 검사. 파일이 많거나(>10개) 청크 합계가 25MB 초과 시 자동으로 분할 호출 후 결과를 머지한다.
 *
 * Cloud Run HTTP/1.x 본문 한도 32 MiB 안에서 안전하게 동작하기 위해
 * 두 기준 중 먼저 도달하는 것으로 청크 분할:
 *   1) 개수 ≥ 10개
 *   2) 누적 크기 ≥ 25MB (multipart overhead 7MB 안전 마진)
 *
 * @param {Array<File>} files
 * @param {string} feature - "chat" | "lab" | "batch_scoring" 등
 * @param {boolean} [isPro=false]
 * @param {Object} [options]
 * @param {(current:number, total:number)=>void} [options.onChunkProgress] - 청크 분할 시 진행률 콜백
 * @returns {Promise<Object>} 사전 검사 결과 (단일 청크와 동일한 형태로 머지됨)
 */
export const preCheckFiles = async (files, feature = 'chat', isPro = false, options = {}) => {
  const { onChunkProgress } = options || {};

  const fallbackNeedsConfirmation = {
    can_proceed: true,
    needs_confirmation: true,
    overall_status: 'warning',
    title: '파일 사전 검사를 확인하지 못했어요',
    message: '파일 내용이 길거나 일부만 분석될 가능성을 확인하지 못했습니다.',
    details: ['네트워크나 서버 상태 때문에 업로드 전 파일 검사를 완료하지 못했어요.'],
    recommendations: ['계속 진행할 수 있지만, 큰 파일이면 앞부분만 분석될 수 있습니다.'],
    files_info: (files || []).map(file => ({
      filename: file?.name || '파일',
      status: 'warning',
      file_size: file?.size || 0,
      issues: ['파일 사전 검사 실패']
    })),
    limits_info: {}
  };

  const fallbackOk = {
    can_proceed: true,
    needs_confirmation: false,
    overall_status: 'ok',
    title: '파일 분석 준비',
    message: '파일 검사를 건너뛰고 진행합니다.',
    details: [],
    recommendations: [],
    files_info: [],
    limits_info: {}
  };

  const fallbackResult = feature === 'inspector' ? fallbackNeedsConfirmation : fallbackOk;

  try {
    if (!files || files.length === 0) return fallbackOk;

    // 청크 분할 기준: 10개 또는 25MB 누적 중 먼저 도달.
    // - 25MB는 Cloud Run 32MiB 한도 - multipart overhead(약 7MB) 안전 마진.
    // - batch_scoring (학생 30명 + 1인당 1~3MB 사진) 케이스 대응.
    const CHUNK_COUNT = 10;
    const CHUNK_BYTES = 25 * 1024 * 1024;

    const chunks = [];
    let currentChunk = [];
    let currentSize = 0;
    for (const f of files) {
      const fileSize = f?.size || 0;
      // 단일 파일이 청크 한도를 넘으면 단독 청크로 처리 (백엔드가 max_file_size로 거부할 책임).
      const wouldExceedBytes = currentSize + fileSize > CHUNK_BYTES;
      const wouldExceedCount = currentChunk.length >= CHUNK_COUNT;
      if ((wouldExceedBytes || wouldExceedCount) && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentSize = 0;
      }
      currentChunk.push(f);
      currentSize += fileSize;
    }
    if (currentChunk.length > 0) chunks.push(currentChunk);

    if (chunks.length === 1) {
      // 단일 청크: 진행률 콜백도 호출해서 일관성 유지
      try { onChunkProgress?.(1, 1); } catch (_) { /* ignore */ }
      const single = await _preCheckFilesSingle(chunks[0], feature, isPro);
      return single || fallbackResult;
    }

    // 다중 청크 — 순차 호출 (진행률 정확도 + 서버 부하 완화).
    // 기존엔 PARALLEL=3 병렬이었으나 batch_scoring 케이스에서는 사용자에게 진행 상황을
    // 보여주는 것이 더 중요하고, 청크 수가 보통 2~4개라 직렬 호출의 latency 비용이 작다.
    const partials = [];
    for (let i = 0; i < chunks.length; i++) {
      try { onChunkProgress?.(i + 1, chunks.length); } catch (_) { /* ignore */ }
      try {
        const r = await _preCheckFilesSingle(chunks[i], feature, isPro);
        if (r) partials.push(r);
      } catch (e) {
        console.warn(`preCheckFiles chunk ${i + 1}/${chunks.length} 실패:`, e);
      }
    }

    if (partials.length === 0) return fallbackResult;

    const totalFiles = files.length;
    const totalSize = files.reduce((sum, f) => sum + (f.size || 0), 0);
    const merged = _mergePreCheckResults(partials, totalFiles, totalSize);
    return merged || fallbackResult;
  } catch (error) {
    console.error('preCheckFiles 청크 호출 실패:', error);
    return fallbackResult;
  }
};